import { useEffect, useMemo, useState } from 'react';
import { estimatePinochle, pinochleBestSuit, pinochlePartnerOf, pinochleTeamOf, sortHand, type PinochleLegal, type PinochleView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const isRedSuit = (s: string) => s === 'H' || s === 'D';

export function PinochleTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as PinochleView;
  const legal = useGame((s) => s.legal) as PinochleLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const teamName = (team: number) => `${name(team)} & ${name(team + 2)}`;
  const myTeam = mySeat !== null ? pinochleTeamOf(mySeat) : 0;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const bidding = view.phase === 'BIDDING';
  const bidTag = (s: number) => (view.bids[s] === null ? null : view.bids[s] === 0 ? t('ta.pass') : t('ta.bidTag', { n: view.bids[s]! }));
  const playing = view.phase === 'PLAYING' || view.phase === 'TRICK_END';
  const declarerTeam = view.declarer !== null ? pinochleTeamOf(view.declarer) : null;
  const passing = Boolean(myTurn && legal?.kind === 'passCards');
  const roundScores = view.roundResults.at(-1);

  useEffect(() => {
    setPicked([]);
  }, [view.phase, view.round]);

  const onCard = (c: string) => {
    if (passing) {
      setPicked((p) => (p.includes(c) ? p.filter((x) => x !== c) : p.length >= 3 ? [...p.slice(1), c] : [...p, c]));
      return;
    }
    if (!myTurn || legal?.kind !== 'play' || !playable.has(c)) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  const teamPill = (team: number) => (
    <span className={`pill team-pill ${team === myTeam ? 'mine' : ''}`} key={team}>
      {team === myTeam ? t('sp.us') : t('sp.them')}: {view.teamScores[team] ?? 0}
      {playing && ` · ${view.counters[team] ?? 0}${declarerTeam === team && view.highBid ? `/${view.highBid.value}` : ''}`}
    </span>
  );

  const meldLine = (s: number) => {
    const m = view.meld[s];
    if (!m) return null;
    return (
      <div key={s} style={{ marginTop: 4 }}>
        <div style={{ fontSize: 'var(--fs-xs)' }}>
          <strong>{s === mySeat ? t('cg.you') : name(s)}</strong> <span className="pill accent">+{m.points}</span>
        </div>
        <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{m.items.length === 0 ? t('pn.noMeld') : m.items.map((i) => `${t(`pn.m.${i.type}` as never)} ${i.points}`).join(' · ')}</div>
      </div>
    );
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.teamScores[pinochleTeamOf(s)] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {mySeat !== null && s === pinochlePartnerOf(mySeat) && <span className="tag">{t('sp.partner')}</span>}
                {bidding && bidTag(s) && <span className="tag">{bidTag(s)}</span>}
                {!bidding && view.declarer === s && view.highBid && <span className="tag seen">{t('ta.bidTag', { n: view.highBid.value })}</span>}
                {playing && <span className="tag">{view.tricksWon[pinochleTeamOf(s)] ?? 0}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('sp.round', { n: view.round })}
            {view.trump && (
              <>
                {' · '}
                {t('ta.trump')} <span style={{ color: isRedSuit(view.trump) ? '#ff8a8a' : undefined, fontSize: '1.2em' }}>{SUIT_SYMBOL[view.trump]}</span>
              </>
            )}
            {view.phase === 'TRUMP' && !myTurn && turn?.seats[0] !== undefined && ` · ${t('ta.choosing', { name: name(turn.seats[0]) })}`}
            {bidding && !myTurn && turn?.seats[0] !== undefined && ` · ${t('table.waitingFor', { name: name(turn.seats[0]) })}`}
            {(view.phase === 'PASS_PARTNER' || view.phase === 'PASS_DECLARER') && !myTurn && turn?.seats[0] !== undefined && ` · ${t('pn.passing', { name: name(turn.seats[0]) })}`}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 6 }} aria-label={t('table.scores')}>
            {teamPill(myTeam)}
            {teamPill(1 - myTeam)}
          </div>
          {view.phase === 'MELD' ? (
            <div className="card glass" style={{ textAlign: 'center', maxHeight: '46vh', overflow: 'auto' }} role="status">
              <strong>{t('pn.meld')}</strong>
              {[0, 1, 2, 3].map(meldLine)}
            </div>
          ) : (
            <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.round}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
              {view.trick.map((p, i) => {
                const dir = seatPosition(p.seat, mySeat, 4).dir;
                const o = OFFSETS[dir]!;
                const off = { x: o.x * k, y: o.y * k };
                const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
                const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
                return (
                  <div
                    key={p.card}
                    className="slot fly"
                    style={{
                      transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                      ['--from-x' as string]: `${off.x * 3}px`,
                      ['--from-y' as string]: `${off.y * 4}px`,
                      ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                      zIndex: i,
                    }}
                  >
                    <PlayingCard id={p.card} size={trickSize} />
                  </div>
                );
              })}
            </div>
          )}
          {view.phase === 'ROUND_END' && roundScores && (
            <div className="card glass" style={{ textAlign: 'center' }}>
              <strong>{t('sp.roundScores', { n: view.round })}</strong>
              <div className="row" style={{ justifyContent: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                {roundScores.map((r, team) => (
                  <span key={team} className={`pill ${r.delta >= 0 ? 'accent' : 'danger'}`}>
                    {team === myTeam ? t('sp.us') : t('sp.them')}: {r.meld}+{r.counters}
                    {r.tricks === 0 ? ` (${t('pn.noTrick')})` : ''} → {r.delta >= 0 ? '+' : ''}{r.delta}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <MySeat
          seat={mySeat !== null ? seats[mySeat] : undefined}
          sub={mySeat !== null ? <>{view.teamScores[myTeam] ?? 0} pts{bidTag(mySeat) && bidding ? ` · ${bidTag(mySeat)}` : ''}</> : null}
        />
      </div>
      <div className="table-bottom">
        {myTurn && legal?.kind === 'bid' && <BidPicker legal={legal} suggested={suggestedBid(view)} onBid={(v) => void act({ type: 'bid', value: v })} onPass={() => void act({ type: 'pass' })} />}
        {myTurn && legal?.kind === 'trump' && <TrumpPicker suits={legal.suits} suggested={pinochleBestSuit(view.hand)} onPick={(suit) => void act({ type: 'trump', suit })} />}
        {passing && legal?.kind === 'passCards' && (
          <div className="bid-picker" role="group" aria-label={t('pn.passTitle')}>
            <div className="bid-head">
              <strong>{legal.role === 'partner' ? t('pn.passToDeclarer') : t('pn.passToPartner')}</strong>
              <span className="dim">{t('pn.pick', { n: picked.length })}</span>
            </div>
            <button className="btn primary" disabled={picked.length !== 3 || acting} onClick={() => void act({ type: 'passCards', cards: [picked[0]!, picked[1]!, picked[2]!] })}>
              {t('pn.passBtn')}
            </button>
          </div>
        )}
        <Hand size="lg" label="Your hand" maxStep={0.55}>
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              dealIndex={bidding ? i : undefined}
              selected={selected === c || picked.includes(c)}
              playable={myTurn && !passing && playable.has(c)}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((team) => (
                <th key={team} className="ellipsis" style={{ maxWidth: 110 }}>{teamName(team)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundResults.map((row, r) => (
              <tr key={r}>
                <td>R{r + 1}</td>
                {row.map((x, team) => (
                  <td key={team} className="num" style={{ color: x.delta < 0 ? 'var(--danger)' : undefined }}>
                    {x.delta >= 0 ? '+' : ''}{x.delta}
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {x.declarer ? `★ ${x.total}/${x.bid}` : x.total}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.teamScores.map((v, team) => (
                <td key={team} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('pn.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}

/** A modest bid suggestion from the hand's estimated team total. */
function suggestedBid(view: PinochleView): number {
  let best = 0;
  for (const s of ['S', 'H', 'D', 'C'] as const) best = Math.max(best, estimatePinochle(view.hand, s));
  return Math.max(view.config.minBid, Math.floor(best - 2));
}

/** Bid picker: a stepper from the lowest open bid, the suggestion starred, a confirm button and a Pass button. */
function BidPicker({ legal, suggested, onBid, onPass }: { legal: Extract<PinochleLegal, { kind: 'bid' }>; suggested: number; onBid: (v: number) => void; onPass: () => void }) {
  const t = useT();
  const clamp = (v: number) => Math.min(legal.max, Math.max(legal.min, v));
  const [value, setValue] = useState(clamp(suggested));
  const [sent, setSent] = useState(false);
  const send = (fn: () => void) => {
    setSent(true);
    fn();
    setTimeout(() => setSent(false), 1500);
  };
  const step = (n: number) => (
    <button key={n} className="btn" style={{ minWidth: 48 }} onClick={() => setValue((v) => clamp(v + n))} aria-label={`${n > 0 ? '+' : ''}${n}`}>
      {n > 0 ? `+${n}` : n}
    </button>
  );
  return (
    <div className="bid-picker" role="group" aria-label={t('pn.bid')}>
      <div className="bid-head">
        <strong>{t('ta.yourBid')}</strong>
        {legal.canPass ? <span className="dim">{t('pn.passHint', { n: legal.min })}</span> : <span className="dim">{t('ta.mustBid')}</span>}
      </div>
      <div className="row" style={{ justifyContent: 'center', alignItems: 'center', gap: 8 }}>
        {step(-5)}
        {step(-1)}
        <strong className="num" style={{ fontSize: '1.6em', minWidth: 64, textAlign: 'center' }} aria-live="polite">
          {value}
          {value === clamp(suggested) && <span style={{ color: 'var(--gold, #f5c542)' }}> ★</span>}
        </strong>
        {step(1)}
        {step(5)}
      </div>
      <div className="row" style={{ gap: 8 }}>
        {legal.canPass && (
          <button className="btn grow" disabled={sent} onClick={() => send(onPass)}>
            {t('ta.pass')}
          </button>
        )}
        <button className="btn primary grow bid-confirm" disabled={sent} onClick={() => send(() => onBid(value))}>
          {t('sp.bidValue', { n: value })}
        </button>
      </div>
    </div>
  );
}

function TrumpPicker({ suits, suggested, onPick }: { suits: string[]; suggested: string; onPick: (suit: 'S' | 'H' | 'D' | 'C') => void }) {
  const t = useT();
  const [sent, setSent] = useState(false);
  return (
    <div className="bid-picker" role="group" aria-label={t('ta.chooseTrump')}>
      <div className="bid-head">
        <strong>{t('ta.chooseTrump')}</strong>
        <span className="dim">★ {t('cb.suggested')}: <b>{SUIT_SYMBOL[suggested]}</b></span>
      </div>
      <div className="bid-chips" role="group">
        {suits.map((s) => (
          <button
            key={s}
            className={`bid-chip ${s === suggested ? 'star' : ''}`}
            style={{ color: isRedSuit(s) ? 'var(--danger)' : undefined, fontSize: '1.6em' }}
            disabled={sent}
            aria-label={s === 'S' ? 'Spades' : s === 'H' ? 'Hearts' : s === 'D' ? 'Diamonds' : 'Clubs'}
            onClick={() => {
              setSent(true);
              onPick(s as 'S' | 'H' | 'D' | 'C');
              setTimeout(() => setSent(false), 1500);
            }}
          >
            {SUIT_SYMBOL[s]}
          </button>
        ))}
      </div>
    </div>
  );
}
